















































import type { PropType } from '@vue/composition-api';
import { computed, defineComponent, watch } from '@vue/composition-api';
import { useEntityStore } from '../../../entity/store/entity.store';
import { useSigningStore } from '../../../signing/store/signing.store';
import type { BankId } from '../../types/bankid';
import { BankIdSigningType, BankIdStep } from '../../types/enums/bankid-se';
import BankIdError from './steps/BankIdError.vue';
import BankIdQR from './steps/BankIdQR.vue';
import BankIdSameDevice from './steps/BankIdSameDevice.vue';
import BankIdSigned from './steps/BankIdSigned.vue';
import BankIdSsn from './steps/BankIdSsn.vue';
import BankIdStart from './steps/BankIdStart.vue';

export default defineComponent({
  components: {
    BankIdStart,
    BankIdError,
    BankIdSigned,
    BankIdSameDevice,
    BankIdQR,
    BankIdSsn,
  },
  props: {
    bankId: {
      type: Object as PropType<BankId>,
      required: true,
    },
    jwt: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const signingStore = useSigningStore();

    const setBankIdState = (payload: { step: BankIdStep; signingType: BankIdSigningType }) =>
      signingStore.setBankIdState(payload.step, payload.signingType);
    const entityStore = useEntityStore();
    const cancelBankIdTransaction = (ssn?: string) =>
      signingStore.cancelBankIdTransaction(entityStore.entity.id, ssn);
    const startBankIdTransaction = (ssn = '') => signingStore.startBankIdTransaction(ssn);

    const isCompleted = () => {
      if (props.bankId.step === BankIdStep.Completed) {
        emit('sign');
      }
    };

    const bankIdError = computed(() => !!props.bankId && props.bankId.error);

    const showStart = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.None &&
        props.bankId.step === BankIdStep.Start
    );

    const showQR = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.QR &&
        props.bankId.step === BankIdStep.Polling
    );

    const showSameDevice = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.SameDevice &&
        [BankIdStep.Start, BankIdStep.Polling].includes(props.bankId.step)
    );

    const showSSN = computed(
      () =>
        props.bankId.signingType === BankIdSigningType.SSN &&
        [BankIdStep.Start, BankIdStep.Polling].includes(props.bankId.step)
    );
    const showSigned = computed(() => props.bankId.step === BankIdStep.Signed);
    const showError = computed(() => props.bankId.step === BankIdStep.Failed);

    const handleSetQR = async () => {
      await startBankIdTransaction();
      if (props.bankId.step !== BankIdStep.Failed) {
        setBankIdState({ step: BankIdStep.Polling, signingType: BankIdSigningType.QR });
      }
    };

    const handleSetSameDevice = async () => {
      await startBankIdTransaction();
      if (props.bankId.step !== BankIdStep.Failed) {
        setBankIdState({
          step: BankIdStep.Polling,
          signingType: BankIdSigningType.SameDevice,
        });
      }
    };

    const handleSetSSN = () => {
      cancelBankIdTransaction();
      setBankIdState({ step: BankIdStep.Start, signingType: BankIdSigningType.SSN });
    };

    const handleReset = async (ssn = '') => {
      await cancelBankIdTransaction(ssn);
      setBankIdState({ step: BankIdStep.Start, signingType: BankIdSigningType.None });
    };

    const handleStartSSN = async (ssn = '') => {
      setBankIdState({ step: BankIdStep.Polling, signingType: BankIdSigningType.SSN });
      await startBankIdTransaction(ssn);
    };

    watch(() => props.bankId.step, isCompleted);

    return {
      BankIdStep,
      isCompleted,
      bankIdError,
      showStart,
      showQR,
      showSameDevice,
      showSSN,
      showSigned,
      showError,
      handleSetQR,
      handleSetSameDevice,
      handleSetSSN,
      handleReset,
      handleStartSSN,
      setBankIdState,
      cancelBankIdTransaction,
      startBankIdTransaction,
    };
  },
});
